import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Briefcase, ArrowRight, CheckCircle2, MessageSquarePlus, Play, Loader2, X,
} from "lucide-react";
import { useTenant } from "@/context/TenantContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SUBMISSION_STATUS } from "@/lib/strings";
import { NextStepsPanel } from "@/components/NextStepsPanel";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const QUESTIONNAIRE_STATUS = {
  NOT_STARTED: "Non iniziato",
  IN_PROGRESS: "In corso",
  COMPLETED: "Completato",
};
const SCORE_STATUS_LABEL = {
  CURRENT: "aggiornato", STALE: "da ricalcolare", CALCULATING: "calcolo in corso",
  FAILED: "calcolo non riuscito", NOT_CALCULATED: "non calcolato",
  PROVISIONAL: "provvisorio", CALCULATED: "calcolato", APPROVED: "approvato",
  INSUFFICIENT_DATA: "dati insufficienti", SUPERSEDED: "sostituito",
  UNDER_REVIEW: "in revisione", LOCKED: "bloccato",
};
const PLAN_STATUS_LABEL = {
  DRAFT: "bozza", UNDER_REVIEW: "in revisione", APPROVED: "approvato",
  IN_PROGRESS: "in corso", COMPLETED: "completato", ARCHIVED: "archiviato",
};
const TARGET_TYPES = {
  QUESTION: "Domanda questionario",
  DATA: "Dato",
  DOCUMENT: "Documento",
  OTHER: "Altro",
};

export default function Portfolio() {
  const { changeCompany } = useTenant();
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [reqFormFor, setReqFormFor] = useState(null);
  const [targetType, setTargetType] = useState("DATA");
  const [targetCode, setTargetCode] = useState("");
  const [comment, setComment] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/consultant/portfolio");
      setPortfolio(data.portfolio);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const review = async (row, action) => {
    if (!row.period) return;
    setBusy(`${row.company_id}:${action}`);
    try {
      await api.post(
        `/companies/${row.company_id}/periods/${row.period.id}/submission/review`,
        { action });
      toast.success(action === "ACCEPT" ? "Compilazione accettata per l'analisi"
        : action === "START_REVIEW" ? "Revisione avviata" : "Integrazioni richieste");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const sendRequest = async (row) => {
    if (!comment.trim()) return;
    setBusy(`${row.company_id}:req`);
    try {
      await api.post(
        `/companies/${row.company_id}/periods/${row.period.id}/integration-requests`,
        { target_type: targetType, target_code: targetCode.trim() || null, comment: comment.trim() });
      toast.success("Richiesta di integrazione inviata all'azienda");
      setReqFormFor(null);
      setComment("");
      setTargetCode("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const openCompany = (row) => {
    changeCompany(row.company_id);
    navigate("/dashboard");
  };

  return (
    <div className="space-y-6" data-testid="portfolio-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">Portafoglio clienti</h1>
        <p className="text-sm text-slate-600 mt-1">
          Le aziende a te assegnate: stato compilazione, invii da revisionare e richieste di integrazione.
        </p>
      </div>

      <NextStepsPanel />

      {error && (
        <Card className="border-red-200 bg-red-50" data-testid="portfolio-error">
          <CardContent className="p-4 text-sm text-red-700">{error}</CardContent>
        </Card>
      )}

      {!portfolio ? (
        <p className="text-sm text-slate-500">Caricamento…</p>
      ) : portfolio.length === 0 ? (
        <Card className="border-slate-200"><CardContent className="p-8 text-center" data-testid="portfolio-empty">
          <Briefcase className="w-8 h-8 text-slate-300 mx-auto mb-3" aria-hidden="true" />
          <p className="text-sm text-slate-600">Nessuna azienda assegnata al momento.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3" data-testid="portfolio-list">
          {portfolio.map((row) => {
            const sub = SUBMISSION_STATUS[row.submission_status] || SUBMISSION_STATUS.DRAFT;
            const reviewable = ["SUBMITTED_BY_COMPANY", "UNDER_CONSULTANT_REVIEW"].includes(row.submission_status);
            return (
              <Card key={row.company_id} className="border-slate-200" data-testid={`portfolio-row-${row.company_id}`}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-bold text-slate-900">{row.denominazione}</p>
                        <Badge className={sub.cls} data-testid={`portfolio-submission-${row.company_id}`}>{sub.label}</Badge>
                        {row.consultant_role && (
                          <Badge variant="outline" className="text-xs">{row.consultant_role}</Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {row.period ? `Esercizio ${row.period.year} · ${row.period.status}` : "Nessun periodo"}
                        {" · Questionario: "}{QUESTIONNAIRE_STATUS[row.questionnaire_status] || row.questionnaire_status}
                      </p>
                    </div>
                    <div className="flex items-center gap-4 gap-y-2 text-center flex-wrap">
                      <div><p className="text-lg font-extrabold text-red-700" data-testid={`portfolio-missing-${row.company_id}`}>{row.missing}</p><p className="text-[10px] text-slate-500">mancanti</p></div>
                      <div><p className="text-lg font-extrabold text-amber-700" data-testid={`portfolio-toverify-${row.company_id}`}>{row.to_verify}</p><p className="text-[10px] text-slate-500">da verificare</p></div>
                      <div><p className="text-lg font-extrabold text-sky-700" data-testid={`portfolio-requests-${row.company_id}`}>{row.open_requests}</p><p className="text-[10px] text-slate-500">richieste aperte</p></div>
                      {row.mef && (
                        <div data-testid={`portfolio-mef-${row.company_id}`} title={row.mef.p1_missing_count ? `P1 mancanti: ${row.mef.p1_missing.join(", ")}` : "Tutti gli indicatori P1 compilati"}>
                          <p className="text-lg font-extrabold text-green-800" data-testid={`portfolio-mef-pct-${row.company_id}`}>{row.mef.percentage}%</p>
                          <p className="text-[10px] text-slate-500">MEF banche · <span className={row.mef.p1_missing_count ? "text-red-700 font-semibold" : ""} data-testid={`portfolio-mef-p1-${row.company_id}`}>{row.mef.p1_missing_count} P1 mancanti</span></p>
                        </div>
                      )}
                      <div>
                        <p className="text-lg font-extrabold text-green-800" data-testid={`portfolio-score-${row.company_id}`}>
                          {["INSUFFICIENT_DATA", "CALCULATING", "FAILED"].includes(row.score_display_status) ? "—" : row.score ?? "—"}
                        </p>
                        <p className={`text-[10px] ${row.score_display_status === "STALE" ? "text-amber-700 font-semibold" : row.score_display_status === "INSUFFICIENT_DATA" ? "text-red-700 font-semibold" : "text-slate-500"}`}
                          data-testid={`portfolio-score-status-${row.company_id}`}>
                          ESG Score{row.score_display_status ? ` · ${SCORE_STATUS_LABEL[row.score_display_status] || row.score_display_status}` : ""}
                        </p>
                      </div>
                      <div>
                        <p className="text-lg font-extrabold text-indigo-800" data-testid={`portfolio-dq-${row.company_id}`}>{row.data_quality ?? "—"}</p>
                        <p className="text-[10px] text-slate-500">Data Quality</p>
                      </div>
                      <div>
                        <p className="text-lg font-extrabold text-slate-800" data-testid={`portfolio-plan-${row.company_id}`}>
                          {row.plan_status ? `${row.plan_open_actions ?? 0}` : "—"}
                        </p>
                        <p className="text-[10px] text-slate-500">
                          Piano{row.plan_status ? ` · ${PLAN_STATUS_LABEL[row.plan_status] || row.plan_status}` : ""}
                          {row.plan_critical_actions ? ` (${row.plan_critical_actions} crit.)` : ""}
                        </p>
                      </div>
                      <div>
                        <p className="text-lg font-extrabold text-slate-800" data-testid={`portfolio-reports-${row.company_id}`}>
                          {row.reports_total ?? 0}
                        </p>
                        <p className="text-[10px] text-slate-500">
                          Report{row.reports_approved ? ` · ${row.reports_approved} appr.` : ""}
                          {row.reports_shared ? ` · ${row.reports_shared} condivisi` : ""}
                        </p>
                      </div>
                      <div>
                        <p className="text-lg font-extrabold text-sky-800" data-testid={`portfolio-extq-${row.company_id}`}>
                          {row.extq_open ?? 0}
                        </p>
                        <p className="text-[10px] text-slate-500">
                          Questionari{row.extq_awaiting_review ? ` · ${row.extq_awaiting_review} da revis.` : ""}
                          {row.extq_overdue ? ` · ${row.extq_overdue} scaduti` : ""}
                        </p>
                      </div>
                      <div>
                        <p className="text-lg font-extrabold text-indigo-800" data-testid={`portfolio-dataroom-${row.company_id}`}>
                          {row.dataroom_active ?? 0}
                        </p>
                        <p className="text-[10px] text-slate-500">
                          Data Room{row.dataroom_expiring ? ` · ${row.dataroom_expiring} in scadenza` : ""}
                          {row.dataroom_revoked ? ` · ${row.dataroom_revoked} revocate` : ""}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button size="sm" variant="outline" className="h-8 text-xs"
                        onClick={() => openCompany(row)} data-testid={`portfolio-open-${row.company_id}`}>
                        Apri azienda <ArrowRight className="w-3 h-3 ml-1" aria-hidden="true" />
                      </Button>
                      {reviewable && row.submission_status === "SUBMITTED_BY_COMPANY" && (
                        <Button size="sm" variant="outline" className="h-8 text-xs"
                          disabled={busy} onClick={() => review(row, "START_REVIEW")}
                          data-testid={`review-start-${row.company_id}`}>
                          <Play className="w-3 h-3 mr-1" aria-hidden="true" />Avvia revisione
                        </Button>
                      )}
                      {reviewable && (
                        <>
                          <Button size="sm" className="h-8 text-xs bg-green-700 hover:bg-green-800 text-white"
                            disabled={busy} onClick={() => review(row, "ACCEPT")}
                            data-testid={`review-accept-${row.company_id}`}>
                            <CheckCircle2 className="w-3 h-3 mr-1" aria-hidden="true" />Accetta
                          </Button>
                          <Button size="sm" variant="outline"
                            className="h-8 text-xs text-amber-700 border-amber-300 hover:bg-amber-50"
                            disabled={busy}
                            onClick={() => setReqFormFor(reqFormFor === row.company_id ? null : row.company_id)}
                            data-testid={`review-request-changes-${row.company_id}`}>
                            <MessageSquarePlus className="w-3 h-3 mr-1" aria-hidden="true" />Richiedi integrazioni
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  {reqFormFor === row.company_id && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2"
                      data-testid={`integration-form-${row.company_id}`}>
                      <p className="text-xs font-semibold text-amber-900">
                        Richiesta di integrazione — l'azienda vedrà solo questo testo
                      </p>
                      <div className="flex gap-2 flex-wrap">
                        <Select value={targetType} onValueChange={setTargetType}>
                          <SelectTrigger className="w-[180px] h-8 bg-white" data-testid={`integration-type-${row.company_id}`} aria-label="Tipo richiesta">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(TARGET_TYPES).map(([k, v]) => (
                              <SelectItem key={k} value={k}>{v}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <input type="text" value={targetCode} onChange={(e) => setTargetCode(e.target.value)}
                          placeholder="Riferimento (es. VSME.B6.WATER.TOTAL)" maxLength={100}
                          className="flex-1 min-w-[200px] h-8 rounded-md border border-slate-300 bg-white px-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-green-600"
                          data-testid={`integration-code-${row.company_id}`} aria-label="Riferimento" />
                      </div>
                      <div className="flex gap-2">
                        <input type="text" value={comment} onChange={(e) => setComment(e.target.value)}
                          placeholder="Es. Manca il consumo idrico 2025" maxLength={1000}
                          className="flex-1 h-8 rounded-md border border-slate-300 bg-white px-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-green-600"
                          data-testid={`integration-comment-${row.company_id}`} aria-label="Commento per l'azienda" />
                        <Button size="sm" className="h-8 text-xs bg-amber-600 hover:bg-amber-700 text-white"
                          disabled={busy === `${row.company_id}:req` || !comment.trim()}
                          onClick={() => sendRequest(row)}
                          data-testid={`integration-send-${row.company_id}`}>
                          {busy === `${row.company_id}:req` ? <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" /> : "Invia richiesta"}
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 text-xs"
                          onClick={() => setReqFormFor(null)} data-testid={`integration-cancel-${row.company_id}`}>
                          <X className="w-3 h-3" aria-hidden="true" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
